import { useRef as useLocalityRef } from 'react';
import { LocalityFilter } from 'rentalui-finance';
import { useLocation as useRecoveryLocation } from 'react-router-dom';
import { client as recoveryClient } from '../api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Nav, Row, Spinner, Tab } from 'react-bootstrap';
import { usePagedArray, Pager } from 'rentalui-finance';
import ResponsiveTable from '../components/ResponsiveTable';
import SidePanelFilters from '../components/SidePanelFilters';
import ColumnToggle, { useHiddenColumns } from '../components/ColumnToggle';
import DeliveryPoolPricingModal, { poolPricingSummary } from '../components/DeliveryPoolPricingModal';
import UserRolesEditor from '../components/UserRolesEditor';
import {
  adminCreateUser, adminListBookings, adminListItems, adminListUsers, adminUpdateServiceArea,
  adminResetTempPassword,
  adminSetUserStatus, adminListDeliveryPersonRequests, adminDecideDeliveryPersonRequest,
  adminListRatings, adminDecideRating,
  markRefundSent, reconcileBookingPayment, updateItem
} from '../api';
import StarRating from '../components/StarRating';
import MarkPaymentReceivedModal from '../components/MarkPaymentReceivedModal';
import CollapsibleFilters from '../components/CollapsibleFilters';
import InfoTip from '../components/InfoTip';
import { DEPOSIT_STATUS_HELP, PAYMENT_STATE_HELP } from '../components/statusHelp';
import { useAuth } from '../auth';
import FinanceApp from 'rentalui-finance';
import ConfirmModal from '../components/ConfirmModal';
import { API_BASE_URL } from '../api';
import BusinessAnalytics from '../components/BusinessAnalytics';
import DeliveryList from '../components/DeliveryList';
import AddressAutocomplete from '../components/AddressAutocomplete';
import MarkRefundSentModal from '../components/MarkRefundSentModal';
import OrderDetailsModal from '../components/OrderDetailsModal';
import ProductDetailsModal from '../components/ProductDetailsModal';
import UserDetailsModal from '../components/UserDetailsModal';
import BookingActions from '../components/BookingActions';
import CancelBookingModal from '../components/CancelBookingModal';
import CancellationDetailsModal from '../components/CancellationDetailsModal';
import AssignDeliveryModal from '../components/AssignDeliveryModal';
import OnboardingPanel from '../components/OnboardingPanel';
import PlatformSettingsPanel from '../components/PlatformSettingsPanel';
import CompanyProfilePanel from '../components/CompanyProfilePanel';
import DashboardTabs from '../components/DashboardTabs';
import PlansAdminPanel from '../components/PlansAdminPanel';
import SupportTicketsPanel from '../components/SupportTicketsPanel';
import EmailsPanel from '../components/EmailsPanel';
import HomepageRankModal from '../components/HomepageRankModal';

// Story 15 - same badge color convention as OwnerDashboard.js's Refund Pending badge.
const ALL_ROLES = ['customer', 'owner', 'admin', 'delivery', 'finance_employee', 'finance_lead', 'finance_manager'];
const ROLE_LABEL = {
  customer: 'Customer', owner: 'Rental owner', admin: 'Website owner', delivery: 'Delivery',
  finance_employee: 'Finance (view)', finance_lead: 'Finance lead', finance_manager: 'Finance manager'
};

// Story 15 - same badge color convention as OwnerDashboard.js's Refund Pending badge.
const DEPOSIT_BADGE_VARIANT = { REFUND_PENDING: 'warning', REFUNDED: 'success', PARTIALLY_DEDUCTED: 'info' };

// admin_listings_visibility.md - "which company / brand" is vertical-specific and lives in the
// generic attributes bag; check the common keys, fall back to the category.
const companyOf = (item) => {
  const a = item.attributes || {};
  return a.company || a.brand || a.make || a.manufacturer || a.chain || item.category || '—';
};
const ITEM_STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'disabled', label: 'Disabled' },
  { key: 'all', label: 'All' }
];

const emptyFilters = {
  id: '', customer: '', owner: '', item: '', status: '',
  deliveryStatus: '', returnStatus: '', depositStatus: '', payoutStatus: ''
};

// role defaults to 'delivery' - user_management_and_delivery_kyc.md: a delivery account also needs
// Aadhaar KYC + an address, collected below when role === 'delivery'.
const emptyStaffUser = {
  name: '', email: '', password: '', phone: '', role: 'delivery', serviceArea: '',
  aadhaarNumber: '', aadhaarCardUrl: '', selfieUrl: '',
  drivingLicenceNumber: '', drivingLicenceUrl: '',
  line1: '', line2: '', area: '', city: '', state: '', pincode: '',
  lat: null, lng: null, placeId: '', formatted: ''
};

// Address fields lifted in/out of the flat newStaffUser state for AddressAutocomplete.
const STAFF_ADDRESS_KEYS = ['line1', 'line2', 'area', 'city', 'state', 'pincode', 'lat', 'lng', 'placeId', 'formatted'];

// Story 16 - inline-editable per-row so an admin doesn't have to recreate a delivery account just
// to fix a typo'd or newly-expanded coverage area.
function ServiceAreaCell({ user, onSaved }) {
  const [value, setValue] = useState(user.serviceArea || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleBlur = async () => {
    if (value === (user.serviceArea || '')) return;
    setSaving(true);
    setSaved(false);
    try {
      await adminUpdateServiceArea(user._id, value.trim());
      setSaved(true);
      onSaved();
    } catch {
      // best-effort - the field just keeps its typed value if the save fails
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form.Control
      size="sm"
      placeholder="Not set"
      value={value}
      onChange={(e) => { setValue(e.target.value); setSaved(false); }}
      onBlur={handleBlur}
      disabled={saving}
      title={saved ? 'Saved' : undefined}
    />
  );
}

// Server-side prev/next pager shared by the Vehicles and Bookings lists.
function ServerPager({ pagination, page, onPage, noun }) {
  return (
    <div className="d-flex justify-content-between align-items-center small text-muted mt-2">
      <span>{pagination.total} {noun}</span>
      <span>
        <Button size="sm" variant="outline-secondary" className="me-1"
          disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</Button>
        Page {pagination.page}
        <Button size="sm" variant="outline-secondary" className="ms-1"
          disabled={!pagination.hasMore} onClick={() => onPage(page + 1)}>Next</Button>
      </span>
    </div>
  );
}

// Optional columns the admin can hide on the bookings / vehicles lists (ColumnToggle.js).
const BOOKING_OPT_COLS = [
  { key: 'owner', label: 'Owner' },
  { key: 'dates', label: 'Dates' },
  { key: 'return', label: 'Return' },
  { key: 'deposit', label: 'Deposit' }
];
const ITEM_OPT_COLS = [
  { key: 'posted', label: 'Posted' },
  { key: 'company', label: 'Company' },
  { key: 'location', label: 'Location' }
];

export default function AdminDashboard() {
  const [adminLocality, setAdminLocality] = useState('');
  const [adminLocalities, setAdminLocalities] = useState([]);
  const adminLocalityRef = useLocalityRef('');
  const recoveryLocation = useRecoveryLocation();
  useEffect(() => {
    const params = new URLSearchParams(recoveryLocation.search);
    const id = params.get('recoveryCase');
    const bookingId = params.get('bookingId');
    if (!id && !bookingId) return undefined;
    let alive = true;
    recoveryClient.get(bookingId ? `/api/order-attention/orders/jewelry/${encodeURIComponent(bookingId)}` : `/api/order-attention/${id}/details`).then(response => {
      if (bookingId) response = { data: { data: { order: response.data.data } } };
      if (alive && response.data.data.order) setDetailsBooking(response.data.data.order);
    }).catch(() => { if (alive) setError('Unable to open the requested order. Return to its recovery page and retry.'); });
    return () => { alive = false; };
  }, [recoveryLocation.search]);

  const { user, token } = useAuth();
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState(emptyFilters);
  const [newStaffUser, setNewStaffUser] = useState(emptyStaffUser);
  const [userMsg, setUserMsg] = useState('');
  const [userErr, setUserErr] = useState('');
  const [refundSentBookingId, setRefundSentBookingId] = useState(null); // Story 15
  const [paymentBooking, setPaymentBooking] = useState(null); // fix_issue3 follow-up - record/correct payment receipt
  const [detailsBooking, setDetailsBooking] = useState(null); // fix_issues.md follow-up
  const [confirmReject, setConfirmReject] = useState(null);
  const [cancelBookingId, setCancelBookingId] = useState(null); // v3_fix_issues #12
  const [viewCancellationBooking, setViewCancellationBooking] = useState(null); // v3_fix_issues #12
  const [assignDeliveryBooking, setAssignDeliveryBooking] = useState(null); // v3_fix_issues #12
  const [itemStatusFilter, setItemStatusFilter] = useState('active'); // admin_listings_visibility.md
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsPagination, setItemsPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [bookingsPage, setBookingsPage] = useState(1);
  const [bookingsPagination, setBookingsPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [productItemId, setProductItemId] = useState(null); // product-details popup on a listing
  // Column show/hide (ColumnToggle.js) - persisted per browser. Owner hidden by default.
  const bookingCols = useHiddenColumns('rv:jewelry:adminBookingsCols', ['owner']);
  const itemCols = useHiddenColumns('rv:jewelry:adminItemsCols', []);
  // user_management_and_delivery_kyc.md
  const [userFilters, setUserFilters] = useState({ status: 'active', role: '', q: '', page: 1, limit: 20 });
  const [userPagination, setUserPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userDetailsId, setUserDetailsId] = useState(null);
  const [usersTab, setUsersTab] = useState('accounts'); // accounts | addstaff | requests
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [poolUser, setPoolUser] = useState(null); // "Manage pool & pricing" modal
  const [rolesUser, setRolesUser] = useState(null); // inline "Roles" quick-edit modal
  const [deliveryRequests, setDeliveryRequests] = useState([]);
  const [deliveryReqFilter, setDeliveryReqFilter] = useState('pending');
  const [reqSearch, setReqSearch] = useState('');
  const [pendingReqCount, setPendingReqCount] = useState(0);
  const [tempPasswordMsg, setTempPasswordMsg] = useState('');
  // Per-request "also serve other businesses" toggle for the Approve action, seeded from the
  // owner's proposal (common/v3/delivery_agent_pool_scoping.md).
  const [sharedOnApprove, setSharedOnApprove] = useState({});
  // fix_issue3.md #3 - ratings moderation queue.
  const [ratings, setRatings] = useState([]);
  const [ratingFilter, setRatingFilter] = useState('pending');

  const isAdmin = user?.role === 'admin' || (user?.roles || []).includes('admin');
  const isFinanceRole = ['finance_employee', 'finance_lead', 'finance_manager']
    .some((r) => user?.role === r || (user?.roles || []).includes(r));

  // v3_fix_issues #14 - workflow tabs first, analytics/ratings/etc. behind a "More" menu; admin
  // and finance see different sets. Controlled so a "More" item can drive the same panes.
  const [activeTab, setActiveTab] = useState(isAdmin ? 'bookings' : (isFinanceRole ? 'finance' : 'bookings'));

  const loadBookings = useCallback(async (activeFilters, page = 1) => {
    const cleaned = Object.fromEntries(Object.entries(activeFilters).filter(([, v]) => v));
    const res = await adminListBookings({ ...cleaned, location: adminLocalityRef.current, page, limit: 20 });
    setBookings(res.data);
    setBookingsPagination({ page: res.pagination.page, total: res.pagination.total, hasMore: res.pagination.hasMore });
  }, []);

  const loadUsers = useCallback(async (f) => {
    const cleaned = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '' && v != null));
    const res = await adminListUsers(cleaned);
    setUsers(res.data);
    setUserPagination({ page: res.pagination.page, total: res.pagination.total, hasMore: res.pagination.hasMore });
    if (res.pendingDeliveryRequests != null) setPendingReqCount(res.pendingDeliveryRequests);
  }, []);

  const loadDeliveryRequests = useCallback(async (status) => {
    setDeliveryRequests(await adminListDeliveryPersonRequests(status === 'all' ? undefined : status));
  }, []);

  const loadRatings = useCallback(async (status) => {
    setRatings(await adminListRatings(status === 'all' ? undefined : status));
  }, []);

  const handleDecideRating = async (id, decision) => {
    try {
      await adminDecideRating(id, decision);
      loadRatings(ratingFilter);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update that rating.');
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    try {
      await Promise.all([
        loadItems(itemStatusFilter, 1),
        loadUsers(userFilters),
        loadDeliveryRequests(deliveryReqFilter),
        loadRatings(ratingFilter),
        loadBookings(filters, 1)
      ]);
    } catch (err) {
      setError('Could not load the admin dashboard.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBookings, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  // ---- user_management_and_delivery_kyc.md handlers ----
  const applyUserFilters = (patch) => {
    const next = { ...userFilters, ...patch, page: patch.page || 1 };
    setUserFilters(next);
    loadUsers(next).catch(() => setError('Could not load users.'));
  };
  const handleToggleUserStatus = async (u) => {
    try {
      await adminSetUserStatus(u._id, u.status === 'disabled' ? 'active' : 'disabled');
      loadUsers(userFilters);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to change the account status.');
    }
  };
  const toggleSelectUser = (id) => setSelectedUserIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAllOnPage = () => setSelectedUserIds((prev) => {
    const selectable = users.filter((u) => u._id !== user?.id).map((u) => u._id);
    const allOn = selectable.every((id) => prev.has(id));
    return allOn ? new Set() : new Set(selectable);
  });
  const bulkSetStatus = async (status) => {
    setError('');
    try {
      await Promise.all([...selectedUserIds].map((id) => adminSetUserStatus(id, status)));
      setSelectedUserIds(new Set());
      loadUsers(userFilters);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to update some accounts.');
    }
  };
  const handleResetPassword = async (u) => {
    setTempPasswordMsg('');
    setError('');
    try {
      const res = await adminResetTempPassword(u._id);
      setTempPasswordMsg(`New temporary password for ${u.email}: ${res.tempPassword} (shown once — pass it on; they must change it at next sign-in).`);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to reset the password.');
    }
  };
  const handleDeliveryRequestFilter = (e) => {
    setDeliveryReqFilter(e.target.value);
    loadDeliveryRequests(e.target.value);
  };
  const handleDecideDeliveryRequest = async (id, decision, availableForOtherOwners) => {
    setTempPasswordMsg('');
    try {
      const res = await adminDecideDeliveryPersonRequest(id, decision, '', availableForOtherOwners);
      if (res.tempPassword) {
        setTempPasswordMsg(`Account created for ${res.createdUser.email}. Temporary password: ${res.tempPassword} (shown once — pass it on).`);
      }
      loadDeliveryRequests(deliveryReqFilter);
      loadUsers(userFilters);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to process that request.');
    }
  };

  // admin_listings_visibility.md - a listing is taken down by disabling it, not deleting it
  // (fix_issues.md #6/#19), and re-enabled by the same toggle. PATCH .../items/:id already lets
  // an admin set any status (routes/ownerCrud.js).
  const loadItems = useCallback(async (status, page = 1) => {
    try {
      const res = await adminListItems({ status, location: adminLocalityRef.current, page, limit: 20 });
      setItems(res.data);
      setAdminLocalities(res.localities || []);
      setItemsPagination({ page: res.pagination.page, total: res.pagination.total, hasMore: res.pagination.hasMore });
      setItemsPage(res.pagination.page);
    } catch {
      setError('Could not load listings.');
    }
  }, []);

  const handleItemStatusFilter = (e) => {
    const next = e.target.value;
    setItemStatusFilter(next);
    loadItems(next, 1);
  };

  const handleToggleItemStatus = async (item) => {
    const next = item.status === 'disabled' ? 'available' : 'disabled';
    await updateItem(item._id, { status: next });
    loadItems(itemStatusFilter, itemsPage);
  };

  // homepage_refactor.md - the admin-only "Verified" badge shown on the homepage Trending strip
  // (see routes/ownerCrud.js, which 403s an owner trying to set this themselves).
  const handleToggleVerified = async (item) => {
    await updateItem(item._id, { verified: !item.verified });
    loadItems(itemStatusFilter, itemsPage);
  };

  // admin_homepage_ranking requirement - website-admin-only "premium placement" (1-100, shared
  // platform-wide) that leads the homepage's cross-vertical featured rail.
  const [rankModalItem, setRankModalItem] = useState(null);
  const handleSaveRank = async (rank) => {
    await updateItem(rankModalItem._id, { homepageRank: rank });
    loadItems(itemStatusFilter, itemsPage);
  };

  // payements_requirements.md - confirm a rental owner's gateway-down manual payment entry.
  const handleReconcile = async (id) => {
    try {
      await reconcileBookingPayment(id);
      loadBookings(filters, bookingsPage);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to reconcile that payment.');
    }
  };

  const handleFilterChange = (field) => (e) => setFilters((prev) => ({ ...prev, [field]: e.target.value }));

  // Bug found live: submitting an invalid filter (e.g. "Owner id" isn't a real Mongo ObjectId -
  // the field expects the owner's account id, not their name) 400s at
  // validators/index.js's adminBookingFilterSchema, and this had no try/catch at all - the
  // rejection went uncaught in the browser console instead of showing anything to the admin.
  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBookingsPage(1);
    try {
      await loadBookings(filters, 1);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to apply those filters.');
    }
  };

  const handleClearFilters = async () => {
    setFilters(emptyFilters);
    setError('');
    setBookingsPage(1);
    try {
      await loadBookings(emptyFilters, 1);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to load bookings.');
    }
  };

  const handleStaffFileUpload = (key, label) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUserErr('');
    try {
      const { uploadImages } = await import('../api');
      const [url] = await uploadImages([file]);
      setNewStaffUser((p) => ({ ...p, [key]: url }));
    } catch {
      setUserErr(`Could not upload the ${label} image (JPEG/PNG).`);
    }
  };

  const handleCreateStaffUser = async (e) => {
    e.preventDefault();
    setUserMsg('');
    setUserErr('');
    const s = newStaffUser;
    const payload = { name: s.name, email: s.email, password: s.password, phone: s.phone, role: s.role, serviceArea: s.serviceArea };
    if (s.role === 'delivery') {
      payload.kyc = {
        aadhaarNumber: s.aadhaarNumber, aadhaarCardUrl: s.aadhaarCardUrl,
        selfieUrl: s.selfieUrl,
        drivingLicenceNumber: s.drivingLicenceNumber, drivingLicenceUrl: s.drivingLicenceUrl
      };
      payload.address = {
        line1: s.line1, line2: s.line2, area: s.area, city: s.city, state: s.state, pincode: s.pincode,
        lat: s.lat ?? null, lng: s.lng ?? null, placeId: s.placeId || '', formatted: s.formatted || ''
      };
    }
    try {
      await adminCreateUser(payload);
      setUserMsg(`${ROLE_LABEL[s.role] || s.role} account created.`);
      setNewStaffUser(emptyStaffUser);
      loadUsers(userFilters);
    } catch (err) {
      setUserErr(err.response?.data?.msg || 'Unable to create the account.');
    }
  };

  const ratingsPaged = usePagedArray(ratings, { resetKey: ratingFilter });
  // The requests list is filtered client-side by a small search box in its side panel.
  const filteredRequests = useMemo(() => {
    const q = reqSearch.trim().toLowerCase();
    if (!q) return deliveryRequests;
    return deliveryRequests.filter((r) => `${r.name} ${r.email} ${r.phone || ''}`.toLowerCase().includes(q));
  }, [deliveryRequests, reqSearch]);
  const deliveryRequestsPaged = usePagedArray(filteredRequests, { resetKey: `${deliveryReqFilter}:${reqSearch}` });

  if (loading) return <Spinner animation="border" role="status" />;

  const ratingsPending = ratings.filter((r) => r.status === 'pending').length;
  const primaryNav = isAdmin
    ? [
        ['bookings', `Bookings (${bookingsPagination.total})`],
        ['deliveries', 'Deliveries'],
        ['items', `Pieces (${itemsPagination.total})`]
      ]
    : (isFinanceRole ? [['finance', 'Finance']] : []);
  const moreNav = isAdmin
    ? [
        ['business', 'Business'],
        ['ratings', `Ratings${ratingsPending ? ` (${ratingsPending})` : ''}`],
        // user_management_and_delivery_kyc.md - delivery-person requests are an account-creation
        // flow, so they live inside the Users tab now rather than in a tab of their own.
        ['users', `Users (${userPagination.total})${pendingReqCount ? ` · ${pendingReqCount} request${pendingReqCount > 1 ? 's' : ''}` : ''}`],
        ['onboarding', 'Onboarding'],
        ['supportTickets', 'Support tickets'],
        ['emails', 'Email log'],
        ['settings', 'Settings'],
        ['companyProfile', 'Company profile'],
        ['plans', 'Subscription'],
        ['finance', 'Finance']
      ]
    : (isFinanceRole ? [['business', 'Business'], ['plans', 'Subscription reviews']] : []);

  return (
    <div>
      <h3 className="mb-3">Admin dashboard</h3>
      {isAdmin && ['items', 'properties', 'bookings', 'today', 'visits', 'cancelled'].includes(activeTab) && <LocalityFilter value={adminLocality} options={adminLocalities} onChange={value => { setAdminLocality(value); adminLocalityRef.current = value; loadItems(itemStatusFilter, 1); loadBookings(filters, 1); }} />}
      {error && <Alert variant="danger">{error}</Alert>}

      <DashboardTabs activeKey={activeTab} onSelect={setActiveTab} primary={primaryNav} more={moreNav}>
        <Tab.Pane eventKey="items">
          <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
            <span className="small text-muted">Show</span>
            <Form.Select aria-label="Listing status" size="sm" style={{ maxWidth: 160 }} value={itemStatusFilter} onChange={handleItemStatusFilter}>
              {ITEM_STATUS_FILTERS.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </Form.Select>
            <span className="small text-muted">Active &amp; most recently posted first.</span>
            <span className="ms-auto">
              <ColumnToggle options={ITEM_OPT_COLS} hidden={itemCols.hidden} onToggle={itemCols.toggle} label="Piece columns" />
            </span>
          </div>
          <ResponsiveTable
            rows={items}
            rowKey={(i) => i._id}
            empty="No listings here."
            rowClassName={(i) => (i.status === 'disabled' ? 'text-muted' : '')}
            columns={[
              {
                key: 'name', header: 'Item', card: 'title',
                cell: (item) => (
                  <>
                    {item.name}
                    {item.category && <div className="small text-muted">{item.category}</div>}
                  </>
                )
              },
              {
                key: 'owner', header: 'Posted by', card: 'subtitle',
                cell: (item) => (item.ownerName
                  ? <>{item.ownerName}{item.ownerEmail && <div className="small text-muted">{item.ownerEmail}</div>}</>
                  : <span className="text-muted">—</span>)
              },
              { key: 'posted', header: 'Posted', hideBelow: 'md', cell: (item) => (item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '—') },
              { key: 'company', header: 'Company', hideBelow: 'md', cell: (item) => companyOf(item) },
              { key: 'location', header: 'Location', hideBelow: 'md', cell: (item) => item.location || '—' },
              { key: 'price', header: 'Price', className: 'text-nowrap', cell: (item) => `₹${item.price}/${item.priceUnit}` },
              {
                key: 'status', header: 'Status', card: 'badge',
                cell: (item) => (
                  <>
                    <Badge bg={item.status === 'available' ? 'success' : item.status === 'disabled' ? 'secondary' : 'info'}>
                      {item.status}
                    </Badge>
                    {item.verified && <Badge bg="success" className="ms-1">✓ Verified</Badge>}
                    {item.homepageRank != null && <Badge bg="warning" text="dark" className="ms-1">★ Rank #{item.homepageRank}</Badge>}
                  </>
                )
              }
            ].filter((c) => !itemCols.hidden.has(c.key))}
            actions={(item) => [
              { label: 'Details', variant: 'link', onClick: () => setProductItemId(item._id) },
              // The website owner edits any listing with the same form its owner used.
              { label: 'Edit', href: `/admin/items/${item._id}/edit` },
              {
                label: item.status === 'disabled' ? 'Enable' : 'Disable',
                variant: item.status === 'disabled' ? 'success' : 'danger',
                onClick: () => handleToggleItemStatus(item)
              },
              // homepage_refactor.md - shown as a badge on the homepage Trending strip.
              {
                label: item.verified ? 'Unverify' : 'Verify',
                variant: item.verified ? 'outline-secondary' : 'success',
                onClick: () => handleToggleVerified(item)
              },
              // admin_homepage_ranking requirement - "premium placement" on the homepage.
              {
                label: item.homepageRank != null ? `Rank #${item.homepageRank}` : 'Set homepage rank',
                variant: item.homepageRank != null ? 'outline-warning' : 'outline-primary',
                onClick: () => setRankModalItem(item)
              }
            ]}
          />
          <ServerPager pagination={itemsPagination} page={itemsPage} noun="pieces"
            onPage={(p) => loadItems(itemStatusFilter, p)} />
        </Tab.Pane>

        <Tab.Pane eventKey="bookings">
          <div className="d-flex justify-content-end mb-2">
            <ColumnToggle options={BOOKING_OPT_COLS} hidden={bookingCols.hidden} onToggle={bookingCols.toggle} />
          </div>
          <CollapsibleFilters label="Filters" className="mb-3">
          <Form onSubmit={handleFilterSubmit} className="p-3 border rounded bg-light">
            <Row className="g-2">
              <Col md={2}><Form.Control size="sm" placeholder="Booking id" value={filters.id} onChange={handleFilterChange('id')} /></Col>
              <Col md={2}><Form.Control size="sm" placeholder="Customer" value={filters.customer} onChange={handleFilterChange('customer')} /></Col>
              <Col md={2}><Form.Control size="sm" placeholder="Owner name or id" value={filters.owner} onChange={handleFilterChange('owner')} /></Col>
              <Col md={2}><Form.Control size="sm" placeholder="Item name" value={filters.item} onChange={handleFilterChange('item')} /></Col>
              <Col md={2}>
                <Form.Select size="sm" value={filters.status} onChange={handleFilterChange('status')}>
                  <option value="">Booking status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="cancelled">Cancelled</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={filters.deliveryStatus} onChange={handleFilterChange('deliveryStatus')}>
                  <option value="">Delivery status</option>
                  {['NOT_REQUIRED', 'PENDING', 'PICKED_UP', 'IN_TRANSIT', 'DELIVERED', 'FAILED', 'CANCELLED'].map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={filters.returnStatus} onChange={handleFilterChange('returnStatus')}>
                  <option value="">Return status</option>
                  {['NOT_DUE', 'RETURN_SCHEDULED', 'PICKED_UP', 'RECEIVED', 'INSPECTION_PENDING', 'COMPLETED', 'CANCELLED'].map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={filters.depositStatus} onChange={handleFilterChange('depositStatus')}>
                  <option value="">Deposit status</option>
                  {['NOT_APPLICABLE', 'HELD', 'REFUND_PENDING', 'REFUNDED', 'PARTIALLY_DEDUCTED'].map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select size="sm" value={filters.payoutStatus} onChange={handleFilterChange('payoutStatus')}>
                  <option value="">Payout status</option>
                  {['NOT_DUE', 'PENDING', 'PROCESSING', 'PAID', 'FAILED'].map((s) => (
                    <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={2} className="d-flex gap-2">
                <Button size="sm" type="submit">Apply</Button>
                <Button size="sm" variant="outline-secondary" onClick={handleClearFilters}>Clear</Button>
              </Col>
            </Row>
          </Form>
          </CollapsibleFilters>

          <ResponsiveTable
            rows={bookings}
            rowKey={(b) => b._id}
            empty="No bookings match."
            columns={[
              {
                key: 'booking', header: 'Booking', card: 'title',
                /* fix_issues.md follow-up - Order ID opens the full details (address + owner
                   details together), same "Details" popup pattern as the Deliveries tab. */
                cell: (booking) => (
                  <Button size="sm" variant="link" className="p-0 align-baseline" onClick={() => setDetailsBooking(booking)}>
                    {booking._id.slice(-6).toUpperCase()}
                  </Button>
                )
              },
              {
                key: 'customer', header: 'Customer', card: 'subtitle',
                cell: (booking) => (<>{booking.customerName}<br /><small className="text-muted rv-email" title={booking.email}>{booking.email}</small></>)
              },
              // fix_issues.md follow-up - which rental owner this order belongs to.
              { key: 'owner', header: 'Owner', cell: (booking) => booking.ownerName || <span className="text-muted">—</span> },
              {
                key: 'dates', header: 'Dates', hideBelow: 'md',
                cell: (booking) => `${new Date(booking.startDate).toLocaleDateString()} – ${new Date(booking.endDate).toLocaleDateString()}`
              },
              {
                key: 'status', header: 'Status', card: 'badge',
                cell: (booking) => (
                  <Badge bg={booking.status === 'confirmed' ? 'success' : booking.status === 'cancelled' ? 'danger' : 'warning'}>
                    {booking.status}
                  </Badge>
                )
              },
              {
                key: 'payment', header: 'Payment',
                cell: (booking) => (
                  <>
                    {booking.paymentSummary?.label || booking.paymentStatus}
                    <InfoTip text={PAYMENT_STATE_HELP[booking.paymentSummary?.state]} />
                    {booking.paymentSummary?.amount != null && (
                      <div className="small text-muted">
                        {`₹${(booking.paymentSummary.amountReceived || 0).toLocaleString('en-IN')}`}
                        {' / '}
                        {`₹${(booking.paymentSummary.amount || 0).toLocaleString('en-IN')}`}
                        {booking.paymentSummary.method ? ` · ${booking.paymentSummary.method.replace(/_/g, ' ')}` : ''}
                      </div>
                    )}
                    {booking.status !== 'cancelled' && booking.paymentSummary?.state !== 'REFUNDED' && (
                      <Button
                        size="sm" variant="link"
                        className={`p-0 me-2${booking.paymentSummary?.receivable ? '' : ' text-muted'}`}
                        onClick={() => setPaymentBooking(booking)}
                      >
                        {booking.paymentSummary?.receivable ? 'Record payment' : 'Edit payment details'}
                      </Button>
                    )}
                    {booking.payment?.needsReconciliation && (
                      <Button size="sm" variant="link" className="p-0" onClick={() => handleReconcile(booking._id)}>
                        Reconcile
                      </Button>
                    )}
                  </>
                )
              },
              { key: 'return', header: 'Return', cell: (booking) => booking.returnStatus?.replace(/_/g, ' ') },
              {
                key: 'deposit', header: 'Deposit',
                /* Story 15 - visible to Admin regardless of returnStatus/booking.status. */
                cell: (booking) => (
                  <>
                    <InfoTip text={DEPOSIT_STATUS_HELP[booking.depositStatus]}>
                      <Badge bg={DEPOSIT_BADGE_VARIANT[booking.depositStatus] || 'secondary'}>
                        {booking.depositStatus?.replace(/_/g, ' ')}
                      </Badge>
                    </InfoTip>
                    {booking.depositStatus === 'REFUND_PENDING' && (
                      <Button size="sm" variant="link" className="p-0 ms-2" onClick={() => setRefundSentBookingId(booking._id)}>
                        Mark sent
                      </Button>
                    )}
                  </>
                )
              }
            ].filter((c) => !bookingCols.hidden.has(c.key))}
            actions={(booking) => (
              /* v3_fix_issues #12 - the website admin manages a booking's status exactly like the
                 rental owner (backend already allows admin on these routes). */
              <BookingActions
                booking={booking}
                isAdmin={isAdmin}
                onChanged={() => loadBookings(filters, bookingsPage)}
                onViewProduct={setProductItemId}
                onRecordPayment={setPaymentBooking}
                onMarkRefundSent={setRefundSentBookingId}
                onViewCancellation={setViewCancellationBooking}
                onAssignDelivery={setAssignDeliveryBooking}
                onCancel={setCancelBookingId}
              />
            )}
          />
          <ServerPager pagination={bookingsPagination} page={bookingsPage} noun="bookings"
            onPage={(p) => { setBookingsPage(p); loadBookings(filters, p); }} />
        </Tab.Pane>

        <Tab.Pane eventKey="users">
          {/* user_management_and_delivery_kyc.md - the account list, the "add a staff account" form
              and the owner request queue are three jobs, so they're three sub-tabs now rather than
              two slide-over panels hanging off a toolbar. */}
          <Nav variant="pills" className="mb-3 gap-1 flex-wrap" activeKey={usersTab} onSelect={(k) => k && setUsersTab(k)}>
            <Nav.Item><Nav.Link eventKey="accounts">Accounts ({userPagination.total})</Nav.Link></Nav.Item>
            <Nav.Item><Nav.Link eventKey="addstaff">Add staff account</Nav.Link></Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="requests">
                Delivery-person requests{pendingReqCount ? <Badge bg="danger" className="ms-2">{pendingReqCount}</Badge> : null}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {tempPasswordMsg && <Alert variant="success" onClose={() => setTempPasswordMsg('')} dismissible>{tempPasswordMsg}</Alert>}

          {usersTab === 'accounts' && (
            <div className="d-flex flex-column flex-md-row-reverse gap-3">
              <SidePanelFilters
                badge={(userFilters.role ? 1 : 0) + (userFilters.q ? 1 : 0) + (userFilters.status !== 'active' ? 1 : 0)}
                onClear={() => { setUserSearchInput(''); applyUserFilters({ status: 'active', role: '', q: '' }); }}
              >
                <Form.Label className="small mb-1">Account status</Form.Label>
                <Form.Select size="sm" className="mb-2" value={userFilters.status} onChange={(e) => applyUserFilters({ status: e.target.value })}>
                  <option value="active">Active</option>
                  <option value="disabled">Inactive / disabled</option>
                  <option value="all">All</option>
                </Form.Select>
                <Form.Label className="small mb-1">Role</Form.Label>
                <Form.Select size="sm" className="mb-2" value={userFilters.role} onChange={(e) => applyUserFilters({ role: e.target.value })}>
                  <option value="">Any role</option>
                  {ALL_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                </Form.Select>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control
                  size="sm" placeholder="Name or email"
                  value={userSearchInput}
                  onChange={(e) => setUserSearchInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') applyUserFilters({ q: userSearchInput.trim() }); }}
                />
                <Button size="sm" className="mt-2 w-100" onClick={() => applyUserFilters({ q: userSearchInput.trim() })}>Search users</Button>
              </SidePanelFilters>

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                {selectedUserIds.size > 0 && (
                  <div className="d-flex align-items-center gap-2 mb-2 p-2 border rounded bg-light flex-wrap">
                    <span className="small">{selectedUserIds.size} selected</span>
                    <Button size="sm" variant="outline-danger" onClick={() => bulkSetStatus('disabled')}>Disable selected</Button>
                    <Button size="sm" variant="outline-success" onClick={() => bulkSetStatus('active')}>Enable selected</Button>
                    <Button size="sm" variant="link" className="p-0" onClick={() => setSelectedUserIds(new Set())}>Clear</Button>
                  </div>
                )}
                <ResponsiveTable
                  rows={users}
                  rowKey={(u) => u._id}
                  empty="No users match."
                  rowClassName={(u) => (u.status === 'disabled' ? 'text-muted' : '')}
                  columns={[
                    {
                      key: 'select', card: 'hidden',
                      header: (
                        <Form.Check
                          type="checkbox" aria-label="Select all on this page"
                          checked={users.length > 0 && users.filter((u) => u._id !== user?.id).every((u) => selectedUserIds.has(u._id))}
                          onChange={toggleSelectAllOnPage}
                        />
                      ),
                      cell: (u) => (
                        <Form.Check
                          type="checkbox" aria-label={`Select ${u.name}`}
                          disabled={u._id === user?.id}
                          checked={selectedUserIds.has(u._id)}
                          onChange={() => toggleSelectUser(u._id)}
                        />
                      )
                    },
                    {
                      key: 'name', header: 'Name', card: 'title',
                      cell: (u) => <>{u.name}<div className="small text-muted rv-email" title={u.email}>{u.email}</div></>
                    },
                    {
                      key: 'roles', header: 'Roles',
                      cell: (u) => (
                        <div className="d-flex flex-wrap gap-1">
                          {(u.effectiveRoles || [u.role]).map((r) => (
                            <Badge key={r} bg="secondary">{ROLE_LABEL[r] || r}</Badge>
                          ))}
                        </div>
                      )
                    },
                    { key: 'phone', header: 'Phone', hideBelow: 'md', cell: (u) => u.phone || '—' },
                    {
                      key: 'serviceArea', header: 'Service area', hideBelow: 'md', hideEmpty: true,
                      cell: (u) => ((u.effectiveRoles || [u.role]).includes('delivery')
                        ? <ServiceAreaCell user={u} onSaved={() => loadUsers(userFilters)} />
                        : null)
                    },
                    {
                      key: 'pool', header: 'Pool & pricing', hideBelow: 'md', hideEmpty: true,
                      cell: (u) => {
                        const summary = poolPricingSummary(u, 'delivery person');
                        return summary ? <span className="small">{summary}</span> : null;
                      }
                    },
                    {
                      key: 'status', header: 'Status', card: 'badge',
                      cell: (u) => <Badge bg={u.status === 'disabled' ? 'danger' : 'success'}>{u.status || 'active'}</Badge>
                    }
                  ]}
                  actions={(u) => {
                    const roles = u.effectiveRoles || [u.role];
                    const showPool = roles.includes('delivery') || roles.includes('owner');
                    return [
                      { label: 'Details', variant: 'link', onClick: () => setUserDetailsId(u._id) },
                      { label: 'Roles', variant: 'link', onClick: () => setRolesUser(u) },
                      showPool && { label: 'Manage pool & pricing', variant: 'link', onClick: () => setPoolUser(u) },
                      { label: 'Reset password', variant: 'link', onClick: () => handleResetPassword(u) },
                      {
                        label: u.status === 'disabled' ? 'Enable' : 'Disable',
                        variant: u.status === 'disabled' ? 'success' : 'danger',
                        disabled: u._id === user?.id,
                        onClick: () => handleToggleUserStatus(u)
                      }
                    ].filter(Boolean);
                  }}
                />

                <div className="d-flex justify-content-between align-items-center small text-muted">
                  <span>{userPagination.total} account(s)</span>
                  <span>
                    <Button size="sm" variant="outline-secondary" className="me-1"
                      disabled={userFilters.page <= 1}
                      onClick={() => applyUserFilters({ page: userFilters.page - 1 })}>Prev</Button>
                    Page {userPagination.page}
                    <Button size="sm" variant="outline-secondary" className="ms-1"
                      disabled={!userPagination.hasMore}
                      onClick={() => applyUserFilters({ page: userFilters.page + 1 })}>Next</Button>
                  </span>
                </div>
              </div>
            </div>
          )}

          {usersTab === 'addstaff' && (
            <div style={{ maxWidth: 680 }}>
              <p className="text-muted small">Delivery/operations and finance accounts aren&apos;t self-serve. A delivery account also needs Aadhaar KYC + an address.</p>
              {userMsg && <Alert variant="success" className="py-2" onClose={() => setUserMsg('')} dismissible>{userMsg}</Alert>}
              {userErr && <Alert variant="danger" className="py-2" onClose={() => setUserErr('')} dismissible>{userErr}</Alert>}
              <Form onSubmit={handleCreateStaffUser}>
                <Form.Group className="mb-2" controlId="staff-role">
                  <Form.Label className="visually-hidden">Role</Form.Label>
                  <Form.Select size="sm" value={newStaffUser.role} onChange={(e) => setNewStaffUser((p) => ({ ...p, role: e.target.value }))}>
                    <option value="delivery">Delivery / operations</option>
                    <option value="finance_employee">Finance employee (view only)</option>
                    <option value="finance_lead">Finance lead (approve up to threshold)</option>
                    <option value="finance_manager">Finance manager (approve any + edit config)</option>
                    <option value="admin">Website owner (admin)</option>
                  </Form.Select>
                </Form.Group>
                <div className="row g-2">
                  <div className="col-md-6"><Form.Control size="sm" placeholder="Name" required value={newStaffUser.name} onChange={(e) => setNewStaffUser((p) => ({ ...p, name: e.target.value }))} /></div>
                  <div className="col-md-6"><Form.Control size="sm" type="email" placeholder="Email" required value={newStaffUser.email} onChange={(e) => setNewStaffUser((p) => ({ ...p, email: e.target.value }))} /></div>
                  <div className="col-md-6"><Form.Control size="sm" type="password" placeholder="Temporary password" required minLength={8} value={newStaffUser.password} onChange={(e) => setNewStaffUser((p) => ({ ...p, password: e.target.value }))} /></div>
                  <div className="col-md-6"><Form.Control size="sm" placeholder="Phone (optional)" value={newStaffUser.phone} onChange={(e) => setNewStaffUser((p) => ({ ...p, phone: e.target.value }))} /></div>
                </div>
                {newStaffUser.role === 'delivery' && (
                  <>
                    <Form.Control size="sm" className="mt-2" placeholder="Service area (e.g. Kothrud, Pune)" value={newStaffUser.serviceArea} onChange={(e) => setNewStaffUser((p) => ({ ...p, serviceArea: e.target.value }))} />
                    <div className="row g-2 mt-1">
                      <div className="col-md-6"><Form.Control size="sm" placeholder="Aadhaar number (12 digits)" required maxLength={12} value={newStaffUser.aadhaarNumber} onChange={(e) => setNewStaffUser((p) => ({ ...p, aadhaarNumber: e.target.value }))} /></div>
                      <div className="col-md-6"><Form.Label className="small mb-0">Aadhaar card</Form.Label><Form.Control size="sm" type="file" accept="image/*" onChange={handleStaffFileUpload('aadhaarCardUrl', 'Aadhaar')} />{newStaffUser.aadhaarCardUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                    </div>
                    {/* staff_selfie_kyc.md - mandatory camera selfie (`capture="user"` = front camera on mobile). */}
                    <div className="row g-2 mt-1">
                      <div className="col-md-6"><Form.Label className="small mb-0">Selfie (camera)</Form.Label><Form.Control size="sm" type="file" accept="image/*" capture="user" onChange={handleStaffFileUpload('selfieUrl', 'selfie')} />{newStaffUser.selfieUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                    </div>
                    {/* v5_fix_issues #1 - mandatory driving-licence photo. */}
                    <div className="row g-2 mt-1">
                      <div className="col-md-6"><Form.Control size="sm" placeholder="Driving-licence number (optional)" value={newStaffUser.drivingLicenceNumber} onChange={(e) => setNewStaffUser((p) => ({ ...p, drivingLicenceNumber: e.target.value }))} /></div>
                      <div className="col-md-6"><Form.Label className="small mb-0">Driving licence</Form.Label><Form.Control size="sm" type="file" accept="image/*" onChange={handleStaffFileUpload('drivingLicenceUrl', 'driving-licence')} />{newStaffUser.drivingLicenceUrl && <span className="small text-success">Uploaded ✓</span>}</div>
                    </div>
                    <AddressAutocomplete
                      size="sm"
                      showState
                      required
                      idPrefix="staff-addr"
                      value={STAFF_ADDRESS_KEYS.reduce((o, k) => ({ ...o, [k]: newStaffUser[k] }), {})}
                      onChange={(a) => setNewStaffUser((p) => ({ ...p, ...a }))}
                    />
                  </>
                )}
                <Button size="sm" type="submit" className="mt-2">Create account</Button>
              </Form>
            </div>
          )}

          {usersTab === 'requests' && (
            <div className="d-flex flex-column flex-md-row-reverse gap-3">
              <SidePanelFilters
                badge={(reqSearch.trim() ? 1 : 0) + (deliveryReqFilter !== 'pending' ? 1 : 0)}
                onClear={() => { setReqSearch(''); setDeliveryReqFilter('pending'); loadDeliveryRequests('pending'); }}
              >
                <Form.Label className="small mb-1">Status</Form.Label>
                <Form.Select
                  size="sm" className="mb-2"
                  value={deliveryReqFilter} onChange={handleDeliveryRequestFilter}
                  aria-label="Delivery request status"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="all">All</option>
                </Form.Select>
                <Form.Label className="small mb-1">Search</Form.Label>
                <Form.Control
                  size="sm" placeholder="Name, email or phone"
                  value={reqSearch} onChange={(e) => setReqSearch(e.target.value)}
                />
              </SidePanelFilters>

              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                <p className="text-muted small">Raised by rental owners. Approving one onboards the delivery account (a temporary password is shown once).</p>
                <ResponsiveTable
                  rows={deliveryRequestsPaged.rows}
                  rowKey={(r) => r._id}
                  empty="No requests."
                  columns={[
                    {
                      key: 'candidate', header: 'Candidate', card: 'title',
                      cell: (r) => (
                        <>
                          {r.name}
                          <div className="small text-muted rv-email" title={r.email}>{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>
                          {r.note && <div className="small text-muted">“{r.note}”</div>}
                        </>
                      )
                    },
                    {
                      key: 'requestedBy', header: 'Requested by', hideBelow: 'md',
                      cell: (r) => (<span className="small">{r.requestedByName || '—'}<br /><span className="text-muted">{r.requestedByEmail}</span></span>)
                    },
                    {
                      key: 'area', header: 'Area / address', hideBelow: 'md',
                      cell: (r) => (
                        <span className="small">
                          {r.serviceArea || '—'}
                          <div className="text-muted">{[r.address?.line1, r.address?.city, r.address?.pincode].filter(Boolean).join(', ')}</div>
                        </span>
                      )
                    },
                    {
                      key: 'aadhaar', header: 'Aadhaar',
                      cell: (r) => (
                        <span className="small">
                          {r.kyc?.aadhaarNumber ? `••• ${String(r.kyc.aadhaarNumber).slice(-4)}` : '—'}
                          {r.kyc?.aadhaarCardUrl && <div><a href={r.kyc.aadhaarCardUrl} target="_blank" rel="noreferrer">card</a></div>}
                          {r.kyc?.selfieUrl && <div><a href={r.kyc.selfieUrl} target="_blank" rel="noreferrer">selfie</a></div>}
                          {r.kyc?.drivingLicenceUrl && <div><a href={r.kyc.drivingLicenceUrl} target="_blank" rel="noreferrer">licence</a></div>}
                        </span>
                      )
                    },
                    {
                      key: 'shared', header: 'Shared?',
                      cell: (r) => (r.status === 'pending' ? (
                        <Form.Check
                          type="checkbox"
                          id={`shared-req-${r._id}`}
                          title="Also serve other businesses on the platform"
                          checked={sharedOnApprove[r._id] ?? !!r.proposedAvailableForOtherOwners}
                          onChange={(e) => setSharedOnApprove((p) => ({ ...p, [r._id]: e.target.checked }))}
                        />
                      ) : (
                        <span className="small text-muted">{r.proposedAvailableForOtherOwners ? 'proposed' : '—'}</span>
                      ))
                    },
                    {
                      key: 'status', header: 'Status', card: 'badge',
                      cell: (r) => (
                        <Badge bg={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'} text={r.status === 'pending' ? 'dark' : undefined}>
                          {r.status}
                        </Badge>
                      )
                    }
                  ]}
                  actions={(r) => (r.status === 'pending' ? [
                    { label: 'Approve', variant: 'success', onClick: () => handleDecideDeliveryRequest(r._id, 'approve', sharedOnApprove[r._id] ?? !!r.proposedAvailableForOtherOwners) },
                    { label: 'Reject', variant: 'danger', onClick: () => setConfirmReject({ kind: 'delivery', id: r._id }) }
                  ] : null)}
                />
                <Pager {...deliveryRequestsPaged} />
              </div>
            </div>
          )}
        </Tab.Pane>

        <Tab.Pane eventKey="ratings">
          <div className="d-flex align-items-center gap-2 mb-3">
            <span className="small text-muted">Show</span>
            <Form.Select
              size="sm" style={{ maxWidth: 160 }} value={ratingFilter}
              onChange={(e) => { setRatingFilter(e.target.value); loadRatings(e.target.value); }}
            >
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </Form.Select>
            <span className="small text-muted">Worst ratings are also flagged to the item&apos;s owner.</span>
          </div>
          <ResponsiveTable
            rows={ratingsPaged.rows}
            rowKey={(r) => r._id}
            empty="No ratings here."
            rowClassName={(r) => (r.rating <= 2 ? 'table-warning' : '')}
            columns={[
              { key: 'item', header: 'Item', card: 'title', cell: (r) => r.itemName || '—' },
              { key: 'rating', header: 'Rating', card: 'subtitle', cell: (r) => <StarRating value={r.rating} /> },
              { key: 'review', header: 'Review', className: 'small', cell: (r) => r.review || <span className="text-muted">—</span> },
              {
                key: 'by', header: 'By', className: 'small',
                cell: (r) => (<>{r.userName || '—'}<div className="text-muted">{new Date(r.createdAt).toLocaleDateString()}</div></>)
              },
              {
                key: 'status', header: 'Status', card: 'badge',
                cell: (r) => (
                  <Badge bg={r.status === 'approved' ? 'success' : r.status === 'rejected' ? 'danger' : 'warning'} text={r.status === 'pending' ? 'dark' : undefined}>
                    {r.status}
                  </Badge>
                )
              }
            ]}
            actions={(r) => [
              { label: 'Approve', variant: 'success', hidden: r.status === 'approved', onClick: () => handleDecideRating(r._id, 'approve') },
              { label: 'Reject', variant: 'danger', hidden: r.status === 'rejected', onClick: () => setConfirmReject({ kind: 'rating', id: r._id }) }
            ]}
          />
          <Pager {...ratingsPaged} />
        </Tab.Pane>

        {/* v3_fix_issues #7 & #8 - the website owner's platform-performance overview. Behind the
            "More" menu (v3_fix_issues #14) - it's analytics, not a workflow queue. */}
        <Tab.Pane eventKey="business">
          <BusinessAnalytics />
        </Tab.Pane>

        <Tab.Pane eventKey="finance">
          <FinanceApp
            embedded
            apiBaseUrl={API_BASE_URL}
            getToken={() => token}
            vertical="jewelry"
            actorRole={user?.role}
            onOpenExternal={(kind, entity) => {
              if (kind === 'booking') {
                setActiveTab('bookings');
                setDetailsBooking(entity);
              }
              if (kind === 'listing') setActiveTab('items');
              if (kind === 'staff') setActiveTab('users');
            }}
          />
        </Tab.Pane>

        {/* Story 14 - admin/website-owner scope: every delivery platform-wide for this vertical.
            mountOnEnter: these load their own data, no need to fetch until actually opened. */}
        <Tab.Pane eventKey="deliveries" mountOnEnter unmountOnExit>
          <DeliveryList showSearch />
        </Tab.Pane>

        {/* leads_business_flow_requirement.md - one page, two tabs: the outbound leads
            pipeline and the inbound "Request a Demo" queue (v3_fix_issues #10) - every demo
            request naming this vertical also auto-appears as a lead. */}
        <Tab.Pane eventKey="onboarding" mountOnEnter unmountOnExit>
          <OnboardingPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="supportTickets" mountOnEnter unmountOnExit>
          <SupportTicketsPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="emails" mountOnEnter unmountOnExit>
          <EmailsPanel scope="admin" />
        </Tab.Pane>
        <Tab.Pane eventKey="settings" mountOnEnter unmountOnExit>
          <PlatformSettingsPanel />
        </Tab.Pane>
        <Tab.Pane eventKey="companyProfile" mountOnEnter unmountOnExit>
          <CompanyProfilePanel />
        </Tab.Pane>
        <Tab.Pane eventKey="plans" mountOnEnter unmountOnExit>
          <PlansAdminPanel />
        </Tab.Pane>
      </DashboardTabs>

      <MarkRefundSentModal
        bookingId={refundSentBookingId}
        show={!!refundSentBookingId}
        onHide={() => setRefundSentBookingId(null)}
        onConfirm={markRefundSent}
        onSent={() => { setRefundSentBookingId(null); loadBookings(filters); }}
      />
      <MarkPaymentReceivedModal
        asAdmin
        booking={paymentBooking}
        show={!!paymentBooking}
        onHide={() => setPaymentBooking(null)}
        onRecorded={() => { setPaymentBooking(null); loadBookings(filters); }}
      />
      <ProductDetailsModal
        itemId={productItemId}
        show={!!productItemId}
        onHide={() => setProductItemId(null)}
        editHref={productItemId ? `/admin/items/${productItemId}/edit` : undefined}
      />
      <UserDetailsModal
        userId={userDetailsId}
        show={!!userDetailsId}
        onHide={() => setUserDetailsId(null)}
        onChanged={() => loadUsers(userFilters)}
      />
      <DeliveryPoolPricingModal
        user={poolUser}
        show={!!poolUser}
        poolKind="delivery person"
        onHide={() => setPoolUser(null)}
        onChanged={() => loadUsers(userFilters)}
      />
      <Modal show={!!rolesUser} onHide={() => setRolesUser(null)} centered>
        <Modal.Header closeButton><Modal.Title as="h5">Roles — {rolesUser?.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {rolesUser && (
            <UserRolesEditor user={rolesUser} onChanged={() => loadUsers(userFilters)} roleLabel={ROLE_LABEL} />
          )}
        </Modal.Body>
      </Modal>

      {/* fix_issues3.md - Order ID -> the shared full order popup (address + rental owner details
          + payment/return/deposit), same component every other order surface uses. */}
      <OrderDetailsModal
        booking={detailsBooking}
        show={!!detailsBooking}
        isAdmin={isAdmin}
        onHide={() => setDetailsBooking(null)}
        onChanged={() => { loadBookings(filters, bookingsPage); setDetailsBooking(null); }}
        onViewProduct={setProductItemId}
        onRecordPayment={(b) => { setDetailsBooking(null); setPaymentBooking(b); }}
        onMarkRefundSent={(id) => { setDetailsBooking(null); setRefundSentBookingId(id); }}
        onViewCancellation={(b) => { setDetailsBooking(null); setViewCancellationBooking(b); }}
        onAssignDelivery={(b) => { setDetailsBooking(null); setAssignDeliveryBooking(b); }}
        onCancel={(id) => { setDetailsBooking(null); setCancelBookingId(id); }}
      />

      {/* v3_fix_issues #12 - the admin gets the same booking-management modals the owner has. */}
      <CancelBookingModal
        bookingId={cancelBookingId}
        show={!!cancelBookingId}
        onHide={() => setCancelBookingId(null)}
        onCancelled={() => { setCancelBookingId(null); loadBookings(filters); }}
      />
      <CancellationDetailsModal
        booking={viewCancellationBooking}
        show={!!viewCancellationBooking}
        onHide={() => setViewCancellationBooking(null)}
      />
      <AssignDeliveryModal
        booking={assignDeliveryBooking}
        show={!!assignDeliveryBooking}
        onHide={() => setAssignDeliveryBooking(null)}
        onAssigned={() => { setAssignDeliveryBooking(null); loadBookings(filters); }}
      />
      <HomepageRankModal
        item={rankModalItem}
        show={!!rankModalItem}
        onHide={() => setRankModalItem(null)}
        onSave={handleSaveRank}
      />
      <ConfirmModal
        show={!!confirmReject}
        onHide={() => setConfirmReject(null)}
        title={confirmReject?.kind === 'rating' ? 'Reject this rating?' : 'Reject this delivery request?'}
        message="This cannot be undone from here."
        confirmLabel="Reject"
        confirmVariant="danger"
        onConfirm={async () => {
          if (!confirmReject) return;
          if (confirmReject.kind === 'rating') await handleDecideRating(confirmReject.id, 'reject');
          else await handleDecideDeliveryRequest(confirmReject.id, 'reject');
          setConfirmReject(null);
        }}
      />
    </div>
  );
}
